/**
 * The first-run storefront wizard, as data.
 *
 * Kept identical to the mobile copy at
 * modules/influencer/components/zap/storefront/storefrontSetup.ts — only the
 * import lines and this comment differ. Change one, copy it across.
 *
 * Why a wizard at all: the old setup screen asked for a name, a URL, a
 * description, a logo AND a banner before it would let anyone press Create.
 * Only the first two are real — POST /storefront/setup requires store_name and
 * store_slug and defaults everything else to "". So a creator was being asked
 * to go and find two images before they could have a shop, and most of them
 * simply left. Here the shop exists after step 1; the rest is decoration they
 * can add now or from Store Settings later.
 */

export const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'CAD'];

/** Every field POST /storefront/setup accepts, minus the ids. */
export interface StorefrontDraft {
  store_name: string;
  store_slug: string;
  store_logo: string;
  store_banner: string;
  store_description: string;
  website_url: string;
  default_currency: string;
  logo_file_name: string;
  logo_file_type: string;
  logo_file_path: string;
  logo_storage_size: string | number;
  banner_file_name: string;
  banner_file_type: string;
  banner_file_path: string;
  banner_storage_size: string | number;
}

export type StorefrontStepId = 'name' | 'look' | 'about' | 'review';

export interface StorefrontStep {
  id: StorefrontStepId;
  /** Shown in the progress line — one word, like the Zap automation wizard. */
  stepLabel: string;
  title: string;
  subtitle: string;
  /** Skippable steps show "Skip" instead of a disabled "Next". */
  optional: boolean;
}

export const STOREFRONT_STEPS: StorefrontStep[] = [
  {
    id: 'name',
    stepLabel: 'Name',
    title: 'What is your shop called?',
    subtitle: 'This is the name shoppers see at the top of your page. You can change it any time.',
    optional: false,
  },
  {
    id: 'look',
    stepLabel: 'Look',
    title: 'Add your logo and cover',
    subtitle: 'A picture makes your shop look like yours. No image handy? Skip — you can add it later.',
    optional: true,
  },
  {
    id: 'about',
    stepLabel: 'About',
    title: 'Tell people what you sell',
    subtitle: 'One line under your name. We have written one for you — change it or keep it.',
    optional: true,
  },
  {
    id: 'review',
    stepLabel: 'Done',
    title: 'Here is your shop',
    subtitle: 'This is exactly what people will see. Happy with it? Open your shop.',
    optional: false,
  },
];

export const slugify = (s: string): string =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

/**
 * A name to start from, so step 1 is a confirmation rather than a blank box.
 * "Priya Sharma" -> "Priya's Shop"; a name already ending in s takes a bare
 * apostrophe ("Chris' Shop").
 */
export function suggestStoreName(displayName?: string): string {
  const raw = (displayName || '').trim().split(/\s+/)[0] || '';
  if (!raw) return '';
  // Profile names are often typed all-lowercase; a shop name shouldn't be.
  const first = raw.charAt(0).toUpperCase() + raw.slice(1);
  return /s$/i.test(first) ? `${first}' Shop` : `${first}'s Shop`;
}

/** A starter line for the About step — never left blank on the page itself. */
export function suggestDescription(storeName: string, displayName?: string): string {
  const rawWho = (displayName || '').trim().split(/\s+/)[0];
  const who = rawWho ? rawWho.charAt(0).toUpperCase() + rawWho.slice(1) : '';
  if (who) return `Handpicked favourites from ${who}. Everything here is something I actually use.`;
  if (storeName) return `Welcome to ${storeName} — a handpicked collection of things I actually use.`;
  return 'A handpicked collection of things I actually use.';
}

export function emptyDraft(opts?: { displayName?: string; isUS?: boolean }): StorefrontDraft {
  const name = suggestStoreName(opts?.displayName);
  return {
    store_name: name,
    store_slug: slugify(opts?.displayName || '') || slugify(name),
    store_logo: '',
    store_banner: '',
    store_description: '',
    website_url: '',
    // Fresh storefronts default to INR — steer US creators to USD, as the old
    // form did with its own effect.
    default_currency: opts?.isUS ? 'USD' : 'INR',
    logo_file_name: '',
    logo_file_type: '',
    logo_file_path: '',
    logo_storage_size: '',
    banner_file_name: '',
    banner_file_type: '',
    banner_file_path: '',
    banner_storage_size: '',
  };
}

/**
 * What blocks Next on a given step, or null when it may proceed.
 * Only the name step can block — that is the whole point of the rewrite.
 */
export function stepError(
  stepId: StorefrontStepId,
  draft: StorefrontDraft,
  slugAvailable: boolean | null
): string | null {
  if (stepId !== 'name') {
    if (stepId === 'about' && draft.website_url && !/^https?:\/\/.+/i.test(draft.website_url)) {
      return 'Please enter a full link starting with http:// or https://';
    }
    return null;
  }
  if (!draft.store_name.trim()) return 'Your shop needs a name.';
  if (!draft.store_slug.trim()) return 'Your shop needs a link.';
  if (draft.store_slug.trim().length < 3) return 'Your link needs at least 3 characters.';
  if (slugAvailable === false) return 'That link is taken — try another or tap the refresh icon.';
  return null;
}

export function canFinish(draft: StorefrontDraft, slugAvailable: boolean | null): boolean {
  return (
    stepError('name', draft, slugAvailable) === null &&
    stepError('about', draft, slugAvailable) === null
  );
}

/** A random-suffixed handle, for when the obvious one is taken. */
export function regeneratedSlug(base: string): string {
  const root = slugify(base) || 'store';
  return `${root}-${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * What the review step lists back. Skipped items are named as skipped rather
 * than hidden, so nobody finishes wondering whether they missed something.
 */
export function reviewRows(draft: StorefrontDraft): { label: string; value: string; missing: boolean }[] {
  return [
    { label: 'Shop name', value: draft.store_name || 'Not set', missing: !draft.store_name },
    { label: 'Logo', value: draft.store_logo ? 'Added' : 'Add later', missing: !draft.store_logo },
    { label: 'Cover image', value: draft.store_banner ? 'Added' : 'Add later', missing: !draft.store_banner },
    { label: 'Description', value: draft.store_description ? 'Added' : 'Add later', missing: !draft.store_description },
    { label: 'Website', value: draft.website_url || 'Add later', missing: !draft.website_url },
  ];
}
